// frontend/src/EvaluatorAssignment.tsx - UPDATED with Authentication Headers

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import { extractErrorMessage, logError } from './utils/errorUtils';

interface EvaluatorAssignmentProps {
  processId: number;
  processName: string;
  onComplete: () => void;
  onBack: () => void;
}

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
}

interface Assignment {
  id: number;
  evaluator_id: number;
  username: string;
  full_name: string;
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      marginBottom: 'var(--spacing-xl)'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-xs)'
    },
    buttonSecondary: {
        background: 'var(--color-surface-variant)',
        color: 'var(--color-text-primary)',
        border: '2px solid var(--color-primary)',
    },
    buttonDanger: {
        background: 'var(--color-error)',
        color: 'white'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-sm)',
      border: '1px solid var(--color-surface-variant)',
      borderRadius: 'var(--border-radius-sm)',
      fontSize: 'var(--font-size-sm)',
      fontFamily: 'var(--font-family-primary)',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box' as const
    },
};

const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, enter: boolean) => {
    e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = enter ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none';
};

const EvaluatorAssignment: React.FC<EvaluatorAssignmentProps> = ({ 
  processId, 
  processName, 
  onComplete, 
  onBack 
}) => {
  const [availableEvaluators, setAvailableEvaluators] = useState<User[]>([]);
  const [assignedEvaluators, setAssignedEvaluators] = useState<Assignment[]>([]);
  const [selectedEvaluators, setSelectedEvaluators] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [assignmentNotes, setAssignmentNotes] = useState<{[key: number]: string}>({});

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [processId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token'); // NEW: Get token
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } }; // NEW: Create headers object

      // Load available evaluators and current assignments in parallel
      const [evaluatorsResponse, assignmentsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/auth/users?role=EVALUATION_PANEL_MEMBER`, authHeaders), // Corrected endpoint
        axios.get(`${API_URL}/api/processes/${processId}/evaluators/`, authHeaders)
      ]);

      setAvailableEvaluators(evaluatorsResponse.data);
      setAssignedEvaluators(assignmentsResponse.data);

    } catch (error) {
      console.error('Failed to load evaluator data:', error);
      showNotification('error', 'Failed to load evaluator information');
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleEvaluatorToggle = (evaluatorId: number) => {
    setSelectedEvaluators(prev => {
      const newSet = new Set(prev);
      if (newSet.has(evaluatorId)) {
        newSet.delete(evaluatorId);
      } else {
        newSet.add(evaluatorId);
      }
      return newSet;
    });
  };

  const assignSelectedEvaluators = async () => {
    if (selectedEvaluators.size === 0) {
      showNotification('error', 'Please select at least one evaluator to assign');
      return;
    }

    setSaving(true);
    try {
      const token = Cookies.get('access_token'); // NEW: Get token
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } }; // NEW: Create headers object

      const assignmentPromises = Array.from(selectedEvaluators).map(evaluatorId => {
        return axios.post(`${API_URL}/api/processes/${processId}/evaluators/`, {
          user_id: evaluatorId
        }, authHeaders);
      });

      await Promise.all(assignmentPromises);
      
      showNotification('success', `Successfully assigned ${selectedEvaluators.size} evaluator(s)`);
      
      // Reload data and clear selections
      await loadData();
      setSelectedEvaluators(new Set());
      setAssignmentNotes({});

    } catch (error: any) {
      console.error('Failed to assign evaluators:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to assign evaluators';
      showNotification('error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (evaluatorId: number, evaluatorName: string) => {
    // Custom modal instead of window.confirm
    const confirmed = true; // Replace with a proper modal in a real app
    if (!confirmed) {
      return;
    }

    try {
      const token = Cookies.get('access_token'); // NEW: Get token
      await axios.delete(`${API_URL}/api/processes/${processId}/evaluators/${evaluatorId}`, {
        headers: { Authorization: `Bearer ${token}` } // NEW: Add header
      });
      showNotification('success', `Removed ${evaluatorName} from process`);
      await loadData(); // Reload to update the lists
    } catch (error) {
      console.error('Failed to remove assignment:', error);
      showNotification('error', 'Failed to remove evaluator assignment');
    }
  };

  const handleNotesChange = (evaluatorId: number, notes: string) => {
    setAssignmentNotes(prev => ({
      ...prev,
      [evaluatorId]: notes
    }));
  };

  // Get evaluators that are available for assignment
  const getAvailableForAssignment = () => {
    const assignedUserIds = new Set(assignedEvaluators.map(a => a.evaluator_id));
    return availableEvaluators.filter(evaluator => !assignedUserIds.has(evaluator.id));
  };

  if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem'}}>
                <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>Loading Evaluator Information...</div>
            </div>
        </div>
      </div>
    );
  }

  const availableForAssignment = getAvailableForAssignment();

  return (
    <div style={styles.mainContainer} className="density-compact">
      {notification && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}>
          {notification.message}
        </div>
      )}

      <div style={styles.contentWrapper}>
        <h1 style={styles.pageTitle}>Evaluator Assignment: {processName}</h1>
        <p style={{ textAlign: 'center', color: 'var(--color-text-on-gradient-secondary)', marginBottom: 'var(--spacing-xl)' }}>
          Process ID: {processId}
        </p>

        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
            
            {/* Available Evaluators */}
            <div style={styles.card}>
              <h2 style={{ marginTop: 0, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)' }}>Available Evaluators</h2>
              {availableForAssignment.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                  <p>All available evaluators have been assigned.</p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 'var(--spacing-lg)', maxHeight: '400px', overflowY: 'auto', paddingRight: 'var(--spacing-md)' }}>
                    {availableForAssignment.map(evaluator => (
                      <div key={evaluator.id} onClick={() => handleEvaluatorToggle(evaluator.id)} style={{ 
                        marginBottom: 'var(--spacing-md)', padding: 'var(--spacing-md)', backgroundColor: 'var(--color-surface-variant)', borderRadius: 'var(--border-radius-md)',
                        border: selectedEvaluators.has(evaluator.id) ? '2px solid var(--color-primary)' : '2px solid var(--color-surface-variant)',
                        cursor: 'pointer', transition: 'all 0.2s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            readOnly
                            checked={selectedEvaluators.has(evaluator.id)}
                            style={{ marginRight: 'var(--spacing-md)', width: '1.2rem', height: '1.2rem' }}
                          />
                          <div>
                            <div style={{ fontWeight: 'var(--font-weight-bold)' }}>{evaluator.full_name}</div>
                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{evaluator.email}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={assignSelectedEvaluators}
                    disabled={selectedEvaluators.size === 0 || isSaving}
                    style={{...styles.button, width: '100%', ...((selectedEvaluators.size === 0 || isSaving) && { background: 'var(--color-surface-variant)', cursor: 'not-allowed' })}}
                  >
                    {isSaving ? 'Assigning...' : `Assign Selected (${selectedEvaluators.size})`}
                  </button>
                </>
              )}
            </div>

            {/* Assigned Evaluators */}
            <div style={styles.card}>
              <h2 style={{ marginTop: 0, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)' }}>Assigned Evaluators ({assignedEvaluators.length})</h2>
              {assignedEvaluators.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                  <p>No evaluators assigned yet.</p>
                </div>
              ) : (
                <div style={{maxHeight: '465px', overflowY: 'auto', paddingRight: '1rem'}}>
                  {assignedEvaluators.map(assignment => (
                    <div key={assignment.evaluator_id} style={{ 
                      marginBottom: 'var(--spacing-md)', padding: 'var(--spacing-md)', backgroundColor: 'var(--color-surface-variant)', borderRadius: 'var(--border-radius-md)',
                      border: '1px solid var(--color-surface-variant)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{assignment.full_name}</div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{assignment.username}</div>
                      </div>
                      <button
                        onClick={() => removeAssignment(assignment.id, assignment.full_name)}
                        style={{...styles.button, ...styles.buttonDanger, padding: '0.5rem 1rem', fontSize: '0.9rem'}}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-xl)', paddingTop: 'var(--spacing-xl)', borderTop: '1px solid var(--color-surface-variant)' }}>
            <button onClick={onBack} style={{...styles.button, ...styles.buttonSecondary}}>← Back to Dashboard</button>
            <button onClick={onComplete} style={{...styles.button, background: 'var(--color-success)'}}>Complete Assignment ✅</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluatorAssignment;
